import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseMoney } from '@makila/fiscal';
import { ArrowDownLeft, ArrowUpRight, Boxes, ClipboardList, FolderTree, Pencil, Plus, Scale, Trash2, Warehouse } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ProductPicker, type PickProduct, useCategories, useWarehouses } from '../components/pickers';
import {
  Alert, Badge, Button, Card, Checkbox, Chips, Empty, errorText, Field, fieldErrors, Input, Loading, Modal, PageHeader, Pagination, SearchInput, Select,
  StatCard, StatusBadge, Table, Td, Textarea, Th, Toolbar, Tr, useConfirm, useToast,
} from '../components/ui';
import { del, get, patch, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, mt, today } from '../lib/format';

// ═══ Modal de movimento manual ═════════════════════════════════════════════
export function StockMovementModal({ open, onClose, product, onSaved }: { open: boolean; onClose: () => void; product?: { id: string; code: string; name: string; unit: string } | null; onSaved?: () => void }) {
  const toast = useToast();
  const whs = useWarehouses();
  const [picked, setPicked] = useState<PickProduct | null>(null);
  const [f, setF] = useState({ kind: 'in' as 'in' | 'out' | 'adjust', warehouseId: '', quantity: '', unitCost: '', note: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ kind: 'in', warehouseId: '', quantity: '', unitCost: '', note: '' }); setErrors({}); setPicked(null); } }, [open]);
  const target = product ?? picked;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target) { toast('Escolha o artigo', 'error'); return; }
    setBusy(true); setErrors({});
    try {
      await post('/stock/movements', { productId: target.id, warehouseId: f.warehouseId || null, kind: f.kind, quantity: f.quantity, unitCost: f.kind === 'in' && f.unitCost ? f.unitCost : null, note: f.note });
      toast('Movimento registado');
      onSaved?.(); onClose();
    } catch (ex) { setErrors(fieldErrors(ex)); toast(errorText(ex), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Movimento de stock" subtitle={product ? `${product.code} — ${product.name}` : 'Entrada, saída ou acerto manual'} icon={<Scale />}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="stock-move" loading={busy}>Registar movimento</Button></>}>
      <form id="stock-move" onSubmit={submit} className="space-y-4" noValidate>
        {!product && <div className="space-y-1.5"><div className="text-[13px] font-medium text-[var(--text-2)]">Artigo</div><ProductPicker value={picked} onChange={setPicked} showCost /></div>}
        <Chips value={f.kind} onChange={(k) => setF({ ...f, kind: k })} options={[{ id: 'in', label: 'Entrada' }, { id: 'out', label: 'Saída' }, { id: 'adjust', label: 'Acerto (±)' }]} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Armazém">
            <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
              <option value="">Armazém principal</option>{whs.data?.items.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
          </Field>
          <Field label={f.kind === 'adjust' ? 'Quantidade (+ sobra / − falta)' : 'Quantidade'} required error={errors.quantity}><Input autoFocus inputMode="decimal" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} trailing={target?.unit} /></Field>
          {f.kind === 'in' && <Field label="Custo unitário (s/ IVA)" hint="Actualiza o custo médio ponderado"><Input inputMode="decimal" value={f.unitCost} onChange={(e) => setF({ ...f, unitCost: e.target.value })} trailing="MT" /></Field>}
        </div>
        <Field label="Motivo" required error={errors.note} hint="Fica no histórico do artigo — ex.: quebra, oferta, consumo interno, correcção">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        {f.kind === 'in' && <Alert tone="info">Compras a fornecedores devem ser registadas em <b>Compras</b>: dão entrada em stock e ficam nas contas a pagar.</Alert>}
      </form>
    </Modal>
  );
}

// ═══ Existências ═══════════════════════════════════════════════════════════
export function StockLevelsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const whs = useWarehouses();
  const [wh, setWh] = useState('');
  const [q, setQ] = useState('');
  const [low, setLow] = useState<'all' | 'low'>(sp.get('alerta') ? 'low' : 'all');
  const [moving, setMoving] = useState(false);
  const qc = useQueryClient();
  const params = new URLSearchParams();
  if (wh) params.set('warehouseId', wh);
  if (q) params.set('q', q);
  if (low === 'low') params.set('low', '1');
  const r = useQuery({ queryKey: ['stock-levels', params.toString()], queryFn: () => get(`/stock/levels?${params}`) });
  const rows = (r.data?.rows ?? []) as any[];
  const lowCount = rows.filter((x) => x.min_stock != null && Number(x.stock) < Number(x.min_stock)).length;
  return (
    <>
      <PageHeader eyebrow="Stock" title="Existências" subtitle="Quantidades e valorização a custo médio ponderado"
        actions={can('stock:create') && <Button icon={<Scale className="size-4" />} onClick={() => setMoving(true)}>Movimento manual</Button>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Valor do stock" icon={<Boxes />} value={`${mt(r.data?.totalValue ?? 0)} MT`} sub={wh ? 'No armazém seleccionado' : 'Todos os armazéns'} />
        <StatCard label="Artigos com stock controlado" value={rows.length} />
        <StatCard label="Abaixo do mínimo" tone={lowCount ? 'warn' : 'good'} value={lowCount} />
      </div>
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-64" value={q} onChange={setQ} placeholder="Pesquisar artigo" />
          <Chips value={low} onChange={setLow} options={[{ id: 'all', label: 'Todos' }, { id: 'low', label: 'Abaixo do mínimo' }]} />
          <Select className="ml-auto h-8 w-52 text-xs" value={wh} onChange={(e) => setWh(e.target.value)} aria-label="Armazém">
            <option value="">Todos os armazéns</option>{whs.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </Select>
        </Toolbar>
        {r.isLoading ? <div className="p-5"><Loading /></div> : !rows.length ? (
          <Empty icon={<Boxes />} title="Sem artigos com controlo de stock">Active "Controlar stock" na ficha dos produtos que quer acompanhar.</Empty>
        ) : (
          <Table>
            <thead><tr><Th>Artigo</Th><Th>Categoria</Th><Th className="text-right">Mínimo</Th><Th className="text-right">Stock</Th><Th className="text-right">Custo médio</Th><Th className="text-right">Valor</Th></tr></thead>
            <tbody>{rows.map((x) => {
              const isLow = x.min_stock != null && Number(x.stock) < Number(x.min_stock);
              return (
                <Tr key={x.id} onClick={() => nav(`/artigos/${x.id}`)}>
                  <Td><div className="font-medium">{x.name}</div><div className="text-xs text-[var(--muted)]">{x.code}</div></Td>
                  <Td className="text-[var(--text-2)]">{x.category ?? '—'}</Td>
                  <Td className="num text-[var(--muted)]">{x.min_stock != null ? Number(x.min_stock) : '—'}</Td>
                  <Td className="num font-semibold">{isLow ? <Badge tone="red">{Number(x.stock)} {x.unit}</Badge> : `${Number(x.stock)} ${x.unit}`}</Td>
                  <Td className="num">{mt(x.avg_cost)}</Td>
                  <Td className="num">{mt(Number(x.stock) * Number(x.avg_cost))}</Td>
                </Tr>
              );
            })}</tbody>
          </Table>
        )}
      </Card>
      <StockMovementModal open={moving} onClose={() => setMoving(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['stock-levels'] }); qc.invalidateQueries({ queryKey: ['stock-movements'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); }} />
    </>
  );
}

// ═══ Movimentos ════════════════════════════════════════════════════════════
export function StockMovementsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const qc = useQueryClient();
  const whs = useWarehouses();
  const [f, setF] = useState({ q: '', kind: '' as '' | 'in' | 'out' | 'adjust', wh: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [moving, setMoving] = useState(false);
  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (f.q) params.set('q', f.q);
  if (f.kind) params.set('kind', f.kind);
  if (f.wh) params.set('warehouseId', f.wh);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  const artigo = sp.get('artigo');
  if (artigo) params.set('productId', artigo);
  const r = useQuery({ queryKey: ['stock-movements', params.toString()], queryFn: () => get(`/stock/movements?${params}`) });
  const upd = (p: Partial<typeof f>) => { setF({ ...f, ...p }); setPage(1); };
  return (
    <>
      <PageHeader eyebrow="Stock" title="Movimentos de stock" subtitle="Entradas, saídas e acertos — automáticos (vendas, compras, inventários) e manuais"
        actions={can('stock:create') && <Button icon={<Plus className="size-4" />} onClick={() => setMoving(true)}>Novo movimento</Button>} />
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-56" value={f.q} onChange={(q) => upd({ q })} placeholder="Artigo ou motivo" />
          <Chips value={f.kind} onChange={(kind) => upd({ kind })} options={[{ id: '', label: 'Todos' }, { id: 'in', label: 'Entradas' }, { id: 'out', label: 'Saídas' }, { id: 'adjust', label: 'Acertos' }]} />
          <div className="ml-auto flex flex-wrap gap-2">
            <Select className="h-8 w-44 text-xs" value={f.wh} onChange={(e) => upd({ wh: e.target.value })} aria-label="Armazém"><option value="">Todos os armazéns</option>{whs.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</Select>
            <Input type="date" className="h-8 w-36 text-xs" value={f.from} onChange={(e) => upd({ from: e.target.value })} aria-label="Desde" />
            <Input type="date" className="h-8 w-36 text-xs" value={f.to} onChange={(e) => upd({ to: e.target.value })} aria-label="Até" />
          </div>
        </Toolbar>
        {artigo && <div className="border-b border-[var(--border)] px-5 py-2 text-xs text-[var(--muted)]">A mostrar só um artigo · <Link className="text-brand-700 underline" to="/stock/movimentos">ver todos</Link></div>}
        {r.isLoading ? <div className="p-5"><Loading /></div> : !r.data?.items.length ? <Empty icon={<Scale />} title="Sem movimentos" /> : (
          <>
            <Table>
              <thead><tr><Th>Data</Th><Th>Artigo</Th><Th>Origem</Th><Th>Armazém</Th><Th className="text-right">Quantidade</Th><Th className="text-right">Custo</Th><Th>Utilizador</Th></tr></thead>
              <tbody>{r.data.items.map((m: any) => (
                <Tr key={m.id} onClick={m.document_id ? () => nav(`/documentos/${m.document_id}`) : m.purchase_id ? () => nav(`/compras/${m.purchase_id}`) : m.inventory_id ? () => nav(`/stock/inventarios/${m.inventory_id}`) : undefined}>
                  <Td className="whitespace-nowrap">{dateTime(m.created_at)}</Td>
                  <Td><div className="font-medium">{m.product_name}</div><div className="text-xs text-[var(--muted)]">{m.code}</div></Td>
                  <Td><span className="flex items-center gap-2">{m.kind === 'in' ? <ArrowDownLeft className="size-4 shrink-0 text-emerald-600" /> : m.kind === 'out' ? <ArrowUpRight className="size-4 shrink-0 text-red-500" /> : <Scale className="size-4 shrink-0 text-blue-500" />}<span className="truncate">{m.note}</span></span></Td>
                  <Td>{m.warehouse}</Td>
                  <Td className={`num font-semibold ${m.kind === 'out' || Number(m.quantity) < 0 ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{m.kind === 'out' ? '−' : Number(m.quantity) > 0 ? '+' : ''}{Number(m.quantity)} {m.unit}</Td>
                  <Td className="num text-[var(--muted)]">{m.unit_cost ? mt(m.unit_cost) : '—'}</Td>
                  <Td className="text-[var(--muted)]">{m.user_name ?? 'Sistema'}</Td>
                </Tr>
              ))}</tbody>
            </Table>
            <Pagination page={page} pageSize={50} total={r.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <StockMovementModal open={moving} onClose={() => setMoving(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['stock-movements'] }); qc.invalidateQueries({ queryKey: ['stock-levels'] }); }} />
    </>
  );
}

// ═══ Inventários ═══════════════════════════════════════════════════════════
export function InventoriesPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const whs = useWarehouses();
  const r = useQuery({ queryKey: ['inventories'], queryFn: () => get('/stock/inventories') });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ warehouseId: '', countDate: today(), notes: '', onlyTracked: true });
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      // Sem escolha explícita vai o armazém que a lista mostra (o primeiro activo,
      // o principal à frente) — antes podia ir o principal mesmo estando inactivo.
      const active = whs.data?.items.filter((w) => w.active) ?? [];
      const res = await post<{ id: string }>('/stock/inventories', { ...f, warehouseId: f.warehouseId || (active.find((w) => w.is_default) ?? active[0])?.id, notes: f.notes || null });
      toast('Inventário aberto'); nav(`/stock/inventarios/${res.id}`);
    } catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHeader eyebrow="Stock" title="Inventários" subtitle="Contagens físicas: o fecho acerta o stock às quantidades contadas"
        actions={can('stock:create') && <Button icon={<ClipboardList className="size-4" />} onClick={() => setOpen(true)}>Novo inventário</Button>} />
      <Card padded={false}>
        {r.isLoading ? <div className="p-5"><Loading /></div> : !r.data?.items.length ? (
          <Empty icon={<ClipboardList />} title="Ainda não fez inventários" action={can('stock:create') && <Button onClick={() => setOpen(true)}>Começar inventário</Button>}>Recomenda-se pelo menos um inventário anual, no fecho do exercício.</Empty>
        ) : (
          <Table>
            <thead><tr><Th>Data da contagem</Th><Th>Armazém</Th><Th>Estado</Th><Th className="text-right">Artigos contados</Th><Th>Aberto por</Th><Th>Fechado</Th></tr></thead>
            <tbody>{r.data.items.map((i: any) => (
              <Tr key={i.id} onClick={() => nav(`/stock/inventarios/${i.id}`)}>
                <Td className="font-medium">{date(i.count_date)}</Td><Td>{i.warehouse}</Td><Td><StatusBadge status={i.status} /></Td>
                <Td className="num">{i.counted} / {i.lines}</Td><Td className="text-[var(--muted)]">{i.opened_by_name}</Td><Td className="text-[var(--muted)]">{i.closed_at ? dateTime(i.closed_at) : '—'}</Td>
              </Tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title="Novo inventário" icon={<ClipboardList />} subtitle="Congela a quantidade teórica de cada artigo para comparar com a contagem."
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button><Button loading={busy} onClick={start}>Abrir inventário</Button></>}>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Armazém"><Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>{whs.data?.items.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</Select></Field>
            <Field label="Data da contagem"><Input type="date" value={f.countDate} onChange={(e) => setF({ ...f, countDate: e.target.value })} /></Field>
          </div>
          <Checkbox label="Só artigos com controlo de stock" checked={f.onlyTracked} onChange={(v) => setF({ ...f, onlyTracked: v })} />
          <Field label="Observações"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        </div>
      </Modal>
    </>
  );
}

export function InventoryPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const r = useQuery({ queryKey: ['inventory', id], queryFn: () => get(`/stock/inventories/${id}`) });
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    if (r.data) setCounts(Object.fromEntries(r.data.lines.map((l: any) => [l.product_id, l.counted_qty != null ? String(Number(l.counted_qty)) : ''])));
  }, [r.data]);
  const lines = useMemo(() => ((r.data?.lines ?? []) as any[]).filter((l) => !q || `${l.name} ${l.code} ${l.barcode ?? ''}`.toLowerCase().includes(q.toLowerCase())), [r.data, q]);
  if (r.isLoading || !r.data) return <Loading />;
  const inv = r.data.inventory;
  const editable = inv.status === 'open' && can('stock:edit');
  const counted = Object.values(counts).filter((v) => v !== '').length;
  const diffValue = (r.data.lines as any[]).reduce((s, l) => counts[l.product_id] ? s + (Number(parseMoney(counts[l.product_id]!) ?? 0) - Number(l.expected_qty)) * Number(l.unit_cost) : s, 0);
  const save = async (silent = false) => {
    setBusy('save');
    try {
      await put(`/stock/inventories/${id}/counts`, { counts: Object.entries(counts).map(([productId, v]) => ({ productId, countedQty: v === '' ? null : v })) });
      if (!silent) toast('Contagens guardadas');
      qc.invalidateQueries({ queryKey: ['inventory', id] });
      return true;
    } catch (e) { toast(errorText(e), 'error'); return false; } finally { setBusy(null); }
  };
  const close = async () => {
    if (!(await confirm({ title: 'Fechar o inventário?', description: <>Serão criados movimentos de acerto para os <b>{counted}</b> artigos contados, igualando o stock à contagem. Artigos não contados ficam como estão. Esta operação não se desfaz.</>, confirmLabel: 'Fechar e acertar stock' }))) return;
    if (!(await save(true))) return;
    setBusy('close');
    try {
      const res = await post(`/stock/inventories/${id}/close`);
      toast(`Inventário fechado — ${res.adjustments} acerto(s)`);
      qc.invalidateQueries();
    } catch (e) { toast(errorText(e), 'error'); } finally { setBusy(null); }
  };
  const cancel = async () => {
    if (!(await confirm({ title: 'Cancelar o inventário?', description: 'As contagens são descartadas e o stock não é alterado.', tone: 'danger', confirmLabel: 'Cancelar inventário' }))) return;
    try { await post(`/stock/inventories/${id}/cancel`); toast('Inventário cancelado'); qc.invalidateQueries(); } catch (e) { toast(errorText(e), 'error'); }
  };

  return (
    <>
      <PageHeader eyebrow={<Link to="/stock/inventarios" className="hover:underline">Inventários</Link>} title={<span className="flex items-center gap-3">Inventário de {date(inv.count_date)} <StatusBadge status={inv.status} /></span>}
        subtitle={`${inv.warehouse}${inv.notes ? ' · ' + inv.notes : ''}`}
        actions={editable && <>
          <Button variant="ghost" onClick={cancel}>Cancelar</Button>
          <Button variant="secondary" loading={busy === 'save'} onClick={() => save()}>Guardar contagens</Button>
          <Button loading={busy === 'close'} onClick={close}>Fechar inventário</Button>
        </>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Artigos contados" value={`${counted} / ${r.data.lines.length}`} />
        <StatCard label="Diferença valorizada" tone={diffValue < 0 ? 'warn' : undefined} value={`${diffValue >= 0 ? '+' : ''}${mt(diffValue)} MT`} sub="Contado − teórico × custo médio" />
        <StatCard label="Estado" value={inv.status === 'open' ? 'Em contagem' : inv.status === 'closed' ? 'Fechado' : 'Cancelado'} />
      </div>
      <Card padded={false}>
        <Toolbar><SearchInput className="w-72" value={q} onChange={setQ} placeholder="Filtrar por artigo ou código de barras" /></Toolbar>
        <Table>
          <thead><tr><Th>Artigo</Th><Th className="text-right">Teórico</Th><Th className="w-40 text-right">Contado</Th><Th className="text-right">Diferença</Th></tr></thead>
          <tbody>{lines.map((l: any) => {
            const v = counts[l.product_id] ?? '';
            const diff = v === '' ? null : Number(parseMoney(v) ?? 0) - Number(l.expected_qty);
            return (
              <tr key={l.product_id}>
                <Td><div className="font-medium">{l.name}</div><div className="text-xs text-[var(--muted)]">{l.code}{l.barcode ? ` · ${l.barcode}` : ''}</div></Td>
                <Td className="num">{Number(l.expected_qty)} {l.unit}</Td>
                <Td className="text-right">{editable
                  ? <Input className="num ml-auto h-8 w-28" inputMode="decimal" aria-label={`Contagem de ${l.name}`} value={v} onChange={(e) => setCounts({ ...counts, [l.product_id]: e.target.value })} />
                  : <span className="tabular-nums">{v || '—'}</span>}</Td>
                <Td className={`num font-medium ${diff === null || diff === 0 ? 'text-[var(--muted)]' : diff < 0 ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{diff === null ? '—' : `${diff > 0 ? '+' : ''}${diff}`}</Td>
              </tr>
            );
          })}</tbody>
        </Table>
      </Card>
    </>
  );
}

// ═══ Armazéns ══════════════════════════════════════════════════════════════
export function WarehousesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const whs = useWarehouses();
  const [edit, setEdit] = useState<any | 'new' | null>(null);
  const [f, setF] = useState({ name: '', isDefault: false, active: true });
  useEffect(() => { if (edit) setF(edit === 'new' ? { name: '', isDefault: false, active: true } : { name: edit.name, isDefault: edit.is_default, active: edit.active }); }, [edit]);
  const save = async () => {
    try {
      if (edit === 'new') await post('/stock/warehouses', f); else await patch(`/stock/warehouses/${edit.id}`, f);
      toast('Armazém guardado'); setEdit(null); qc.invalidateQueries({ queryKey: ['warehouses'] });
    } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <>
      <PageHeader eyebrow="Stock" title="Armazéns" subtitle="Locais onde guarda mercadoria — loja, armazém central, viatura…"
        actions={can('stock:create') && <Button icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>Novo armazém</Button>} />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {whs.data?.items.map((w) => (
          <Card key={w.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-brand-500/10 p-2.5 text-brand-700 dark:text-brand-500"><Warehouse className="size-5" /></div>
                <div><div className="font-semibold">{w.name}</div><div className="text-xs text-[var(--muted)]">Valor em stock {mt(w.value)} MT</div></div>
              </div>
              {can('stock:edit') && <Button size="sm" variant="ghost" iconOnly icon={<Pencil className="size-4" />} aria-label={`Editar ${w.name}`} onClick={() => setEdit(w)} />}
            </div>
            <div className="mt-4 flex gap-2">{w.is_default && <Badge tone="teal">Principal</Badge>}{!w.active && <Badge tone="red">Inactivo</Badge>}</div>
          </Card>
        ))}
      </div>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit === 'new' ? 'Novo armazém' : 'Editar armazém'} size="sm" icon={<Warehouse />}
        footer={<><Button variant="secondary" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={save} disabled={f.name.trim().length < 2}>Guardar</Button></>}>
        <div className="space-y-4">
          <Field label="Nome" required><Input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Checkbox label="Armazém principal" hint="Usado por omissão em vendas e compras" checked={f.isDefault} onChange={(v) => setF({ ...f, isDefault: v })} />
          {edit !== 'new' && <Checkbox label="Activo" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />}
        </div>
      </Modal>
    </>
  );
}

// ═══ Categorias ════════════════════════════════════════════════════════════
export function CategoriesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const cats = useCategories();
  const [edit, setEdit] = useState<any | 'new' | null>(null);
  const [name, setName] = useState('');
  useEffect(() => { if (edit) setName(edit === 'new' ? '' : edit.name); }, [edit]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['categories'] }); qc.invalidateQueries({ queryKey: ['products'] }); };
  const save = async () => {
    try { if (edit === 'new') await post('/stock/categories', { name }); else await patch(`/stock/categories/${edit.id}`, { name }); toast('Categoria guardada'); setEdit(null); refresh(); }
    catch (e) { toast(errorText(e), 'error'); }
  };
  const remove = async (c: any) => {
    if (!(await confirm({ title: `Remover a categoria ${c.name}?`, description: c.products ? `${c.products} artigo(s) ficam sem categoria.` : undefined, tone: 'danger', confirmLabel: 'Remover' }))) return;
    try { await del(`/stock/categories/${c.id}`); toast('Categoria removida'); refresh(); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <>
      <PageHeader eyebrow="Stock" title="Categorias" subtitle="Organize os artigos por família para filtrar e analisar vendas"
        actions={can('products:create') && <Button icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>Nova categoria</Button>} />
      <Card padded={false}>
        {!cats.data?.items.length ? <Empty icon={<FolderTree />} title="Sem categorias" /> : (
          <Table>
            <thead><tr><Th>Categoria</Th><Th className="text-right">Artigos</Th><Th /></tr></thead>
            <tbody>{cats.data.items.map((c) => (
              <tr key={c.id}>
                <Td className="font-medium">{c.name}</Td><Td className="num">{c.products}</Td>
                <Td className="text-right whitespace-nowrap">
                  {can('products:edit') && <Button size="sm" variant="ghost" iconOnly icon={<Pencil className="size-4" />} aria-label="Editar" onClick={() => setEdit(c)} />}
                  {can('products:delete') && <Button size="sm" variant="ghost" iconOnly icon={<Trash2 className="size-4" />} aria-label="Remover" onClick={() => remove(c)} />}
                </Td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit === 'new' ? 'Nova categoria' : 'Editar categoria'} size="sm" icon={<FolderTree />}
        footer={<><Button variant="secondary" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={save} disabled={name.trim().length < 2}>Guardar</Button></>}>
        <Field label="Nome" required><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim().length >= 2 && save()} /></Field>
      </Modal>
    </>
  );
}
